import { Injectable, InjectionToken, inject } from '@angular/core';
import { PaginationDto } from './models/interfaces/pagination-dto';

export const PAGINATION_CACHE_DEFAULT_TTL = new InjectionToken<number>('PAGINATION_CACHE_DEFAULT_TTL', {
  providedIn: 'root',
  factory: () => 60_000,
});

export const PAGINATION_CACHE_MAX_ENTRIES_PER_ROUTE = new InjectionToken<number>('PAGINATION_CACHE_MAX_ENTRIES_PER_ROUTE', {
  providedIn: 'root',
  factory: () => 20,
});

export const PAGINATION_CACHE_HIDDEN_THRESHOLD = new InjectionToken<number>('PAGINATION_CACHE_HIDDEN_THRESHOLD', {
  providedIn: 'root',
  factory: () => 5 * 60_000,
});

interface CacheEntry<T = unknown> {
  data: PaginationDto<T>;
  storedAt: number;
  ttl: number;
}

@Injectable({ providedIn: 'root' })
export class PaginationCacheService {

  private readonly defaultTtl = inject(PAGINATION_CACHE_DEFAULT_TTL);

  private readonly maxEntriesPerRoute = inject(PAGINATION_CACHE_MAX_ENTRIES_PER_ROUTE);

  private readonly hiddenThreshold = inject(PAGINATION_CACHE_HIDDEN_THRESHOLD);

  private readonly store = new Map<string, Map<string, CacheEntry>>();

  private hiddenAt: number | null = null;

  constructor() {
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    }
  }

  public get<T>(route: string, queryHash: string): PaginationDto<T> | null {
    const routeMap = this.store.get(route);
    if (!routeMap) return null;

    const entry = routeMap.get(queryHash) as CacheEntry<T> | undefined;
    if (!entry) return null;

    if (Date.now() - entry.storedAt >= entry.ttl) {
      routeMap.delete(queryHash);
      if (routeMap.size === 0) this.store.delete(route);
      return null;
    }

    routeMap.delete(queryHash);
    routeMap.set(queryHash, entry);

    return entry.data;
  }

  public set<T>(route: string, queryHash: string, data: PaginationDto<T>, ttl?: number): void {
    const effectiveTtl = ttl ?? this.defaultTtl;
    if (effectiveTtl <= 0) return;

    let routeMap = this.store.get(route);
    if (!routeMap) {
      routeMap = new Map<string, CacheEntry>();
      this.store.set(route, routeMap);
    }

    routeMap.delete(queryHash);
    routeMap.set(queryHash, {
      data: data as PaginationDto<unknown>,
      storedAt: Date.now(),
      ttl: effectiveTtl,
    });

    while (routeMap.size > this.maxEntriesPerRoute) {
      const oldestKey = routeMap.keys().next().value;
      if (oldestKey === undefined) break;
      routeMap.delete(oldestKey);
    }
  }

  public clearCacheFor(route: string): void {
    this.store.delete(route);
  }

  public clearAll(): void {
    this.store.clear();
  }

  public stableStringify(value: unknown): string {
    return JSON.stringify(value, this.canonicalReplacer);
  }

  private canonicalReplacer = (_key: string, value: unknown): unknown => {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const sorted: Record<string, unknown> = {};
      for (const k of Object.keys(value as Record<string, unknown>).sort()) {
        sorted[k] = (value as Record<string, unknown>)[k];
      }
      return sorted;
    }
    return value;
  };

  private onVisibilityChange = (): void => {
    if (document.visibilityState === 'hidden') {
      this.hiddenAt = Date.now();
      return;
    }

    if (document.visibilityState === 'visible' && this.hiddenAt !== null) {
      const elapsed = Date.now() - this.hiddenAt;
      this.hiddenAt = null;
      if (elapsed >= this.hiddenThreshold) {
        this.clearAll();
      }
    }
  };

}
